import React, { useCallback, useEffect, useState } from "react";
import GenericButton from "@ui-components/Button/GenericButton";
import { useVersionPolling } from "@hooks/useVersionPolling";

/**
 * Shows a small bottom-right prompt inviting the user to reload when a newer
 * frontend build is detected. It fires on two signals:
 *
 *   1. Reactive  — a ChunkLoadError bubbles up (a lazy-loaded code-split chunk
 *      failed to load because its hashed filename no longer matches the freshly
 *      deployed build on S3). The user is likely already stuck, so we surface
 *      the prompt above the blocking overlay.
 *   2. Proactive — periodic polling (see useVersionPolling) notices that the
 *      deployed index.html now references different asset hashes, i.e. a new
 *      version is live even though nothing has broken yet.
 *
 * Mounted once at the app root so it covers every dynamic import in the app.
 */

const CHUNK_ERROR_PATTERNS = [
    "ChunkLoadError",
    "Loading chunk",
    "Loading CSS chunk",
    "Failed to fetch dynamically imported module",
    "error loading dynamically imported module",
];

function isChunkLoadError(value: unknown): boolean {
    if (!value) return false;
    const err = value as { name?: string; message?: string };
    if (err.name === "ChunkLoadError") return true;
    const message =
        typeof err.message === "string" ? err.message : String(value);
    return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

export default function ChunkReloadPrompt() {
    const [visible, setVisible] = useState(false);
    const show = useCallback(() => setVisible(true), []);

    // Proactive: poll for a newer deployed build.
    useVersionPolling(show);

    // Reactive: surface immediately when a chunk fails to load.
    useEffect(() => {
        const onError = (event: ErrorEvent) => {
            if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
                show();
            }
        };
        const onRejection = (event: PromiseRejectionEvent) => {
            if (isChunkLoadError(event.reason)) {
                show();
            }
        };

        window.addEventListener("error", onError);
        window.addEventListener("unhandledrejection", onRejection);
        return () => {
            window.removeEventListener("error", onError);
            window.removeEventListener("unhandledrejection", onRejection);
        };
    }, [show]);

    if (!visible) return null;

    return (
        <div
            role="alert"
            // z-index sits above the "Processing request..." overlay (z-[9999])
            // so the prompt stays clickable even when a stuck spinner is showing.
            className="
                fixed bottom-4 right-4 z-[10000]
                w-[340px] max-w-[calc(100vw-2rem)]
                bg-semantic-bg-primary
                border border-semantic-border-secondary
                rounded-lg shadow-lg
                p-4
                flex flex-col gap-3
            "
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex flex-col gap-1">
                    <h3 className="text-sm font-semibold text-semantic-text-primary">
                        New version available
                    </h3>
                    <p className="text-sm text-semantic-text-secondary">
                        A new version of the dashboard is available. Reload to
                        get the latest updates.
                    </p>
                </div>
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={() => setVisible(false)}
                    className="
                        -mr-1 -mt-1 p-1 leading-none text-lg
                        text-semantic-text-secondary
                        hover:text-semantic-text-primary
                    "
                >
                    ×
                </button>
            </div>
            <div className="flex justify-end">
                <GenericButton
                    variant="brand"
                    size="sm"
                    iconLeftName="refresh"
                    label="Reload"
                    onClick={() => window.location.reload()}
                />
            </div>
        </div>
    );
}
